import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { TimeMode } from '@types';
import { SiteMeta } from '@src/types/SiteMeta';
import { AlertEntity } from '@src/types/Alerts';
import { formatTimestampForDisplay } from '@utils/timezoneUtils';
import { DataSource, Incident } from '../types';

dayjs.extend(utc);

const DEFAULTS = {
  UNKNOWN_UNIT: 'unknown',
  UNKNOWN_RACK: 'unknown_rack',
  UNKNOWN_TEMPERATURE: 0,
} as const;

interface TopicInfo {
  unitId: string | null;
  rackId: string | null;
  siteId?: string | null;
}

interface IncidentOptions {
  unitId: string;
  rackId: string;
  unitName: string;
  temperature: number;
  timestamp: string;
  dataSource: DataSource;
  isRealTime?: boolean;
  mqttTopic?: string;
}

export function formatTimestamp(
  timestamp: string,
  timeMode?: TimeMode,
  siteMeta?: SiteMeta,
): string {
  return formatTimestampForDisplay(timestamp, timeMode, siteMeta);
}

export function validateTimestamp(timestamp: string | Date | number): string {
  const parsed = dayjs.utc(timestamp);
  return parsed.isValid() ? parsed.toISOString() : dayjs.utc().toISOString();
}

export function getUnitName(unitId: string, siteMeta: SiteMeta): string {
  if (!siteMeta?.Units || !siteMeta?.UnitNames) {
    return `Unit ${unitId}`;
  }

  const unitIndex = siteMeta.Units.indexOf(unitId);
  if (unitIndex >= 0 && siteMeta.UnitNames[unitIndex]) {
    return siteMeta.UnitNames[unitIndex];
  }

  const matchingIndex = siteMeta.Units.findIndex(
    (siteUnit) => siteUnit.includes(unitId) || unitId.includes(siteUnit),
  );

  if (matchingIndex !== -1 && siteMeta.UnitNames[matchingIndex]) {
    return siteMeta.UnitNames[matchingIndex];
  }

  return `Unit ${unitId}`;
}

export function resolveUnitName(
  unitId: string | null | undefined,
  siteMeta: SiteMeta,
): string {
  const id = unitId || DEFAULTS.UNKNOWN_UNIT;
  return getUnitName(id, siteMeta);
}

export function extractTopicInfo(topic: string): TopicInfo {
  return {
    siteId: extractSiteIdFromTopic(topic),
    unitId: extractUnitIdFromTopic(topic),
    rackId: extractRackIdFromTopic(topic),
  };
}

export function extractSiteIdFromTopic(topic: string): string | null {
  const match = topic.match(/\/site\/([^\/]+)\//);
  return match ? match[1] : null;
}

export function extractUnitIdFromTopic(topic: string): string | null {
  const match = topic.match(/\/unit\/([^\/]+)\//);
  return match ? match[1] : null;
}

export function extractRackIdFromTopic(topic: string): string | null {
  const match = topic.match(/\/rack\/([^\/]+)\//);
  return match ? match[1] : null;
}

function createIncidentId(
  unitId: string,
  rackId: string,
  timestamp: string,
  source: DataSource,
): string {
  const random = Math.random().toString(36).substring(2, 11);
  return `${source}-${unitId}-${rackId}-${timestamp}-${random}`;
}

function createIncident(options: IncidentOptions): Incident {
  const {
    unitId,
    rackId,
    unitName,
    temperature,
    timestamp,
    dataSource,
    isRealTime = false,
    mqttTopic,
  } = options;

  const validatedTimestamp = validateTimestamp(timestamp);

  return {
    id: createIncidentId(unitId, rackId, validatedTimestamp, dataSource),
    deviceName: unitName,
    rackId,
    maxCellT: temperature,
    timestamp: validatedTimestamp,
    unitId,
    isRealTime,
    dataSource,
    ...(mqttTopic && { mqttTopic }),
  };
}

export function createIncidentFromAlert(
  alert: AlertEntity,
  siteMeta: SiteMeta,
): Incident {
  const { unitId, rackId } = extractTopicInfo(alert.topic);

  const resolvedUnitId = unitId || DEFAULTS.UNKNOWN_UNIT;
  const resolvedRackId = rackId || DEFAULTS.UNKNOWN_RACK;

  const unitName = getUnitName(resolvedUnitId, siteMeta);
  const temperature = extractTemperatureValue(alert.value);

  return createIncident({
    unitId: resolvedUnitId,
    rackId: resolvedRackId,
    unitName,
    temperature,
    timestamp: alert.ts,
    dataSource: DataSource.ALERTS,
    mqttTopic: alert.topic,
  });
}

function extractTemperatureValue(value: unknown): number {
  if (typeof value === 'number' && !isNaN(value)) {
    return value;
  }

  const parsed = parseFloat(String(value));
  return isNaN(parsed) ? DEFAULTS.UNKNOWN_TEMPERATURE : parsed;
}

export function parseRealTimeTemperature(value: unknown): number | null {
  if (value == null) {
    return null;
  }

  const parsed = parseFloat(String(value));
  return isNaN(parsed) ? null : parsed;
}

export function shouldUpdateIncidentTemperature(
  currentTemp: number,
  realTimeTemp: number | null,
): boolean {
  return realTimeTemp != null && realTimeTemp !== currentTemp;
}

export function updateIncidentWithRealTimeTemperature(
  incident: Incident,
  realTimeData: Record<string, { value: unknown } | null>,
): Incident {
  const topic = incident.mqttTopic;

  if (!topic || !realTimeData[topic]) {
    return incident;
  }

  const realTimeTemp = parseRealTimeTemperature(realTimeData[topic]?.value);

  if (!shouldUpdateIncidentTemperature(incident.maxCellT, realTimeTemp)) {
    return incident;
  }

  return {
    ...incident,
    maxCellT: realTimeTemp as number,
    isRealTime: true,
  };
}

export function deduplicateIncidents(
  incidents: ReadonlyArray<Incident>,
): Incident[] {
  const seen = new Map<string, Incident>();

  for (const incident of incidents) {
    const timestampSecond = dayjs(incident.timestamp)
      .startOf('second')
      .toISOString();
    const key = `${incident.unitId}-${incident.rackId}-${timestampSecond}`;

    const existing = seen.get(key);
    if (!existing || incident.maxCellT > existing.maxCellT) {
      seen.set(key, incident);
    }
  }

  return Array.from(seen.values());
}
